import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sparkles, Save, FileDown, Loader2, Undo2, CheckCircle2, ArrowLeft, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, streamPost } from "@/lib/api";
import { emptyNote, parseSections, notePlainText, noteFullText, SOAP_KEYS } from "@/lib/soap";
import { copyText } from "@/lib/clipboard";
import { useAuth } from "@/context/AuthContext";
import { SessionInfoCard, Card } from "@/components/SessionInfoCard";
import { TargetsCard } from "@/components/TargetsCard";
import { BehaviorsCard } from "@/components/BehaviorsCard";
import { SoapSections } from "@/components/SoapSections";
import { PolishMenu } from "@/components/PolishMenu";
import { VoiceDictation } from "@/components/VoiceDictation";
import { TemplateCard } from "@/components/TemplateCard";
import { AutosaveStatus } from "@/components/AutosaveStatus";
import { VersionHistory } from "@/components/VersionHistory";
import { CoachPanel } from "@/components/CoachPanel";
import { applyCoachFix } from "@/lib/coachFixes";
import { useAutosave } from "@/lib/useAutosave";

const pickSoap = (f) => Object.fromEntries(SOAP_KEYS.map((s) => [s.key, f[s.key]]));

export default function NoteEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(emptyNote());
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState("");
  const [prev, setPrev] = useState(null);
  const [loaded, setLoaded] = useState(!id);

  const [templates, setTemplates] = useState([]);
  const [showData, setShowData] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const streaming = busy === "generate" || busy === "polish";
  const applyFix = (fix) => {
    const r = applyCoachFix(form, fix);
    if (!r) { toast.error("Couldn't find where this fix goes — copy it in by hand"); return false; }
    const before = form[r.key];
    set(r.key, r.value);
    const label = r.key[0].toUpperCase() + r.key.slice(1);
    toast.success(r.mode === "replaced" ? `Updated ${label}` : `Added to ${label}`, {
      action: { label: "Undo", onClick: () => set(r.key, before) },
    });
    return true;
  };
  const onCreated = useCallback((newId) => {
    window.history.replaceState(window.history.state, "", `/notes/${newId}`);
    window.dispatchEvent(new Event("bb-url"));
  }, []);
  const autosave = useAutosave(form, setForm, id, streaming || !loaded, onCreated);
  const { markClean } = autosave;

  useEffect(() => {
    if (!id) return;
    api.get(`/notes/${id}`).then((r) => { setForm(r.data); markClean(r.data); setLoaded(true); })
      .catch((e) => { toast.error(errMsg(e)); navigate("/"); });
  }, [id, navigate, markClean]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onClientChange = (cid) => {
    if (cid === "none") return setForm((f) => ({ ...f, client_id: null, client_name: "" }));
    const c = clients.find((x) => x.id === cid);
    const hasData = form.targets.some((t) => t.total) || form.behaviors.some((b) => b.frequency);
    if (hasData && !window.confirm("Switching client replaces the targets and behaviors you've logged. Continue?")) return;
    setForm((f) => ({
      ...f,
      client_id: cid,
      client_name: c.name,
      targets: c.goals.map((g) => ({ goal_id: g.id, name: g.name, correct: 0, total: 0, prompt_level: "", notes: "" })),
      behaviors: c.behaviors.map((b) => ({ name: b.name, frequency: 0, duration: "", notes: "" })),
    }));
  };

  const runStream = async (kind, path, body) => {
    setPrev(pickSoap(form));
    if (!autosave.checkpointRef.current) autosave.checkpointRef.current = kind === "generate" ? "ai_generate" : "ai_polish";
    setBusy(kind);
    try {
      const full = await streamPost(path, body, (txt) => setForm((f) => ({ ...f, ...parseSections(txt) })));
      setForm((f) => ({ ...f, ...parseSections(full) }));
      toast.success(kind === "generate" ? "SOAP note drafted — review before saving" : "Note polished");
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(""); }
  };

  const generate = () => runStream("generate", "/ai/generate", form);
  const polish = (preset) => {
    if (!SOAP_KEYS.some((s) => form[s.key]?.trim())) return toast.error("Write or generate the note first");
    runStream("polish", "/ai/polish", { note_text: notePlainText(form), preset });
  };

  const save = async (silent = false) => {
    setBusy("save");
    try {
      const data = await autosave.save({ force: true, checkpoint: "manual" });
      if (!silent) toast.success("Note saved");
      return data;
    } catch (e) { toast.error(errMsg(e)); return null; } finally { setBusy(""); }
  };

  const exportPdf = async () => {
    const saved = await save(true);
    if (saved) navigate(`/notes/${saved.id}/print`);
  };

  if (!loaded) return <div className="grid place-items-center h-64"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const template = templates.find((t) => t.code === form.cpt_code);
  const hasData = form.targets.some((t) => t.total) || form.behaviors.some((b) => b.frequency);
  const dataVisible = !template || template.show_data || showData || hasData;

  return (
    <div className="space-y-6 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <button onClick={() => navigate("/")} className="text-sm text-slate-500 hover:text-primary flex items-center gap-1 mb-2" data-testid="back-to-notes"><ArrowLeft className="h-3.5 w-3.5" />All notes</button>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{form.id ? form.client_name || "Session note" : "New SOAP note"}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <AutosaveStatus status={autosave.status} savedAt={autosave.savedAt} />
          <CoachPanel key={id || "new"} form={form} disabled={streaming} onApply={applyFix} />
          {form.id && (
            <VersionHistory
              noteId={form.id}
              disabled={!!busy}
              flush={() => autosave.save().catch(() => {})}
              onRestored={(data) => { setForm(data); markClean(data); setPrev(null); }}
            />
          )}
          <label className="flex items-center gap-2 text-sm text-slate-600 mr-2">
            <Switch checked={form.status === "final"} onCheckedChange={(v) => set("status", v ? "final" : "draft")} data-testid="note-final-switch" />
            {form.status === "final" ? <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" />Final</span> : "Draft"}
          </label>
          <Button variant="outline" className="rounded-full" disabled={!!busy || !SOAP_KEYS.some((s) => form[s.key]?.trim())}
            onClick={() => copyText(noteFullText({ ...form, provider_name: form.provider_name || user?.name, provider_role: form.provider_role || user?.role }, template), "Full note copied — paste it into your medical platform")}
            data-testid="copy-note-button">
            <Copy className="h-4 w-4 mr-2" />Copy note
          </Button>
          <Button variant="outline" className="rounded-full" onClick={exportPdf} disabled={!!busy} data-testid="export-pdf-button"><FileDown className="h-4 w-4 mr-2" />Export PDF</Button>
          <Button className="rounded-full px-6" onClick={() => save()} disabled={!!busy} data-testid="save-note-button">
            {busy === "save" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-5">
          <SessionInfoCard form={form} set={set} clients={clients} onClientChange={onClientChange} />
          <TemplateCard template={template} values={form.template_fields || {}} onChange={(v) => set("template_fields", v)} />
          {dataVisible ? (
            <>
              <TargetsCard targets={form.targets} onChange={(v) => set("targets", v)} />
              <BehaviorsCard behaviors={form.behaviors} onChange={(v) => set("behaviors", v)} />
            </>
          ) : (
            <button onClick={() => setShowData(true)} className="w-full rounded-xl border border-dashed bg-white py-3 text-sm text-slate-500 hover:text-primary hover:border-primary/40 transition-colors" data-testid="show-data-cards-button">
              + Add trial or behavior data to this note
            </button>
          )}
          <Card title="What happened this session" action={<VoiceDictation onText={(t) => setForm((f) => ({ ...f, session_details: f.session_details ? `${f.session_details}\n${t}` : t }))} />}>
            <Textarea
              value={form.session_details}
              onChange={(e) => set("session_details", e.target.value)}
              placeholder="Quick notes in your own words — caregiver report, mood, activities, reinforcers, anything notable. Or tap Dictate and just talk."
              className="min-h-[150px] text-[15px] leading-relaxed"
              data-testid="session-details-textarea"
            />
          </Card>
        </div>

        <div className="lg:col-span-7">
          <div className="lg:sticky lg:top-6 space-y-4">
            <div className="rounded-xl bg-[#0B3F44] text-white p-5 flex flex-wrap items-center justify-between gap-4 relative overflow-hidden grain">
              <div className="relative">
                <div className="font-display font-semibold text-lg">AI SOAP draft</div>
                <div className="text-sm text-teal-100/80">Uses only the data you entered — nothing invented.</div>
              </div>
              <div className="relative flex gap-2">
                {prev && !streaming && (
                  <Button variant="ghost" className="rounded-full text-teal-50 hover:bg-white/10 hover:text-white" onClick={() => { setForm((f) => ({ ...f, ...prev })); setPrev(null); }} data-testid="undo-ai-button">
                    <Undo2 className="h-4 w-4 mr-1" />Undo
                  </Button>
                )}
                <PolishMenu onPick={polish} disabled={!!busy} />
                <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" onClick={generate} disabled={!!busy} data-testid="ai-generate-soap-button">
                  {busy === "generate" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Generate SOAP
                </Button>
              </div>
            </div>
            <SoapSections values={form} onChange={set} streaming={streaming} />
          </div>
        </div>
      </div>
    </div>
  );
}
